import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, EMPTY, Observable, Subject } from 'rxjs';
import { catchError, filter, map, mapTo, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

import {
    ActionItem,
    ActionItemStyle,
    ApiQuery,
    DialogStyle,
    EmptyStructuredQuery,
    FilterBarTranslations,
    OpenDirection,
    ShDynamicDialogService,
    ShUrlQueryService,
    StructuredQuery
} from '@stylehaven/components';
import {
    LoadSavedQueryDialogOutput,
    LoadSavedQueryDialogOutputSuccess
} from '../../../entities/saved-queries/load-saved-query-dialog.entity';
import { SavedQuery } from '../../../entities/saved-queries/saved-query.entity';
import { LuceneParser } from '../../../helpers/lucene-parser.helper';
import { savedQueryToStructuredQuery } from '../../helpers/saved-query-load.helper';
import { LoadSavedQueryDialogComponent } from '../../../saved-queries/components/load-saved-query-dialog/load-saved-query.component';
import { SaveQueryDialogComponent } from '../../../saved-queries/components/save-query-dialog/save-query-dialog.component';
import { SavedQueriesSandboxService } from '../../../saved-queries/sandbox/saved-queries.sandbox';
import { RecordSearch } from '../../entities/record-search.entity';

@Component({
    standalone: false,
    selector: 'digihaven-search-bar',
    templateUrl: './record-search-bar-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecordSearchBarContainerComponent implements OnInit, OnDestroy {
    private readonly destroy$: Subject<void> = new Subject<void>();
    private readonly enableSavedQueries$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    readonly enableAdvancedSearch$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    private readonly recordSearch$: BehaviorSubject<RecordSearch> = new BehaviorSubject<RecordSearch>({});

    filterQuery$: BehaviorSubject<StructuredQuery> = new BehaviorSubject<StructuredQuery>(EmptyStructuredQuery);

    private readonly urlQueryService: ShUrlQueryService = inject(ShUrlQueryService);

    @Input() tag?: string;

    @Input() set enableSavedQueries(enableSavedQuery: boolean) {
        if (enableSavedQuery) {
            this.enableSavedQueries$.next(enableSavedQuery);
        }
    }

    @Input() set enableAdvancedSearch(enableAdvancedSearch: boolean) {
        this.enableAdvancedSearch$.next(enableAdvancedSearch);
    }

    @Input() set recordSearch(recordSearch: RecordSearch) {
        if (recordSearch) {
            this.recordSearch$.next(recordSearch);
        }
    }

    @Output() queryChanges: EventEmitter<ApiQuery> = new EventEmitter<ApiQuery>();

    searchBarI18n!: FilterBarTranslations;
    searchBarActionItems$!: Observable<ActionItem[]>;

    private savedQueryActions$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    private refreshSavedQueriesActionItems$!: Observable<boolean>;

    constructor(
        private readonly translateService: TranslateService,
        private savedQueriesSandbox: SavedQueriesSandboxService,
        private dynamicDialogService: ShDynamicDialogService,
        private luceneParser: LuceneParser
    ) {
        this.setI18n();
    }

    ngOnInit(): void {
        this.initSavedQueryActionRefresh();
        this.initFilterBarActionItems();
        this.initStructuredQueryListener();
    }

    private initSavedQueryActionRefresh(): void {
        this.refreshSavedQueriesActionItems$ = this.savedQueryActions$.pipe(mapTo(true), startWith(true));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.enableSavedQueries$.complete();
        this.recordSearch$.complete();
    }

    private setI18n(): void {
        this.searchBarI18n = {
            filterAction: this.translateService.instant('components.filter.filter-select.label'),
            filterDate: {
                year: this.translateService.instant('components.filter.date.year'),
                month: this.translateService.instant('components.filter.date.month'),
                day: this.translateService.instant('components.filter.date.day'),
                range: this.translateService.instant('components.filter.date.range'),
                from: this.translateService.instant('components.filter.date.from'),
                until: this.translateService.instant('components.filter.date.until'),
                specific: this.translateService.instant('components.filter.date.specific'),
                submit: this.translateService.instant('components.filter.date.submit'),
                invalidRange: this.translateService.instant('components.filter.date.invalidRange'),
                incompleteDate: this.translateService.instant('components.filter.date.incompleteDate')
            },
            filterSelect: {
                loading: this.translateService.instant('components.filter.loading'),
                noneSelected: this.translateService.instant('components.filter.none')
            },
            filterMultiSelect: {
                loading: this.translateService.instant('components.filter.loading'),
                multipleSelected: this.translateService.instant('components.filter.multiple'),
                allSelected: this.translateService.instant('components.filter.multiple'),
                noneSelected: this.translateService.instant('components.filter.none')
            }
        };
    }

    private initStructuredQueryListener(): void {
        // SearchBar sends to Url directly.
        // We send saved query to Url directly here
        // MH subscribes to this in overview
        // As a DH shortcut we subscribe and send it back to overview from here
        this.urlQueryService
            .getStructuredQuery()
            .pipe(takeUntil(this.destroy$))
            .subscribe(structuredQuery => {
                this.queryChanges.next(new ApiQuery(structuredQuery));
            });
    }

    private initFilterBarActionItems(): void {
        const totalSavedQueries$: Observable<number> = combineLatest([
            this.enableSavedQueries$,
            this.refreshSavedQueriesActionItems$
        ]).pipe(
            filter(([enableSavedQueries, refreshSavedQueriesActionItems]) => enableSavedQueries),
            map(([enableSavedQueries, refreshSavedQueriesActionItems]) => refreshSavedQueriesActionItems),
            switchMap(() => this.savedQueriesSandbox.getTotalSavedQueries())
        );

        this.searchBarActionItems$ = combineLatest([
            this.enableSavedQueries$,
            totalSavedQueries$,
            this.recordSearch$
        ]).pipe(
            map(([enableSavedQueries, totalSavedQueries, recordSearch]) =>
                this.getFilterBarActionItems(enableSavedQueries, totalSavedQueries, recordSearch)
            )
        );
    }

    private getFilterBarActionItems(
        enableSavedQueries: boolean,
        totalSavedQueries: number,
        recordSearch: RecordSearch
    ): ActionItem[] {
        const actionItems: ActionItem[] = [];

        if (enableSavedQueries) {
            const savedQueriesActionItem: ActionItem = this.getSavedQueriesActionItem(totalSavedQueries, recordSearch);
            actionItems.push(savedQueriesActionItem);
        }

        return actionItems;
    }

    private getSavedQueriesActionItem(totalSavedQueries: number, recordSearch: RecordSearch): ActionItem {
        const actionItems: ActionItem[] = [];

        const cleanedQuery: string = recordSearch.Q?.replace('*:*', '') ?? '';

        const saveQueryAction: ActionItem = {
            label: this.translateService.instant('components.saved-queries.actions.save'),
            eventListener: () => this.onSaveQueryAction(recordSearch),
            disabled: cleanedQuery.length === 0,
            disabledInfoMsg: this.translateService.instant('components.saved-queries.disabled.save')
        };

        actionItems.push(saveQueryAction);

        if (totalSavedQueries > 0) {
            const loadSavedQueryAction: ActionItem = {
                label: this.translateService.instant('components.saved-queries.actions.load'),
                eventListener: this.onLoadSavedQueryAction.bind(this)
            };

            actionItems.push(loadSavedQueryAction);
        }

        return {
            label: '',
            icon: 'save',
            style: ActionItemStyle.PureSquareIconButtonLight,
            actionItems,
            openDirection: OpenDirection.BelowLeft
        };
    }

    private onSaveQueryAction(recordSearch: RecordSearch): void {
        const cleanedRecordSearch: RecordSearch = {
            ...recordSearch,
            Q: recordSearch.Q?.replace('*:*', '')
        };
        this.openSaveQueryDialog(cleanedRecordSearch).pipe(take(1), takeUntil(this.destroy$)).subscribe();
    }

    private openSaveQueryDialog(recordSearch: RecordSearch): Observable<any> {
        const dialogRef = this.dynamicDialogService.open<SavedQuery>(SaveQueryDialogComponent, {
            title: this.translateService.instant('components.saved-queries.new-saved-query-dialog.title'),
            data: recordSearch.Q,
            options: {
                style: DialogStyle.Wide
            }
        });

        return dialogRef.afterClosed$;
    }

    private onLoadSavedQueryAction(): void {
        this.openLoadSavedQueryDialog()
            .pipe(catchError(() => EMPTY))
            .subscribe(query => this.onLoadedQuery(query));
    }

    private onLoadedQuery(query: SavedQuery): void {
        this.urlQueryService.setStructuredQuery(savedQueryToStructuredQuery(this.luceneParser, query.Query));
    }

    private openLoadSavedQueryDialog(): Observable<SavedQuery> {
        const title: string = this.translateService.instant('components.saved-queries.load-saved-query-dialog.title');
        const cancel: string = this.translateService.instant('actions.common.cancel');
        const search: string = this.translateService.instant('actions.common.search');

        const dialogRef = this.dynamicDialogService.open<LoadSavedQueryDialogOutput>(LoadSavedQueryDialogComponent, {
            title,
            data: { cancel, search },
            options: {
                style: DialogStyle.Wide
            }
        });

        return dialogRef.afterClosed$.pipe(
            filter(val => !!val),
            filter((val: LoadSavedQueryDialogOutput): val is LoadSavedQueryDialogOutputSuccess =>
                val.hasOwnProperty('query')
            ),
            map(output => output.query)
        );
    }
}
